// src/components/CertLabelModal.jsx
//
// Etiqueta del CERTIFICADO para la Brother PT-P750W (cinta de 18 mm).
// Mismo formato que la etiqueta de ID del instrumento (QRLabelModal): QR arriba
// y dos líneas centradas debajo, pero dice "Cert. 6499001" + "Calins" y el QR
// lleva el link corto para descargar el certificado.
//
// El link incluye un token (/c/6499001-a1b2c3d4e5) para que no se puedan
// enumerar los certificados de otros clientes. El token viaja dentro del QR:
// en la etiqueta impresa NO se ve.

import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';

// El sticker se lee en planta del cliente: SIEMPRE apunta a producción, nunca
// al host donde se generó la etiqueta.
const PUBLIC_BASE = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://calib.pixber.pro';

// Cinta 18 mm (alto) · etiqueta compacta de 20 mm (largo) · margen 2 mm
const LABEL_W_MM = 20;
const LABEL_H_MM = 18;
const MARGIN_MM = 2;
const QR_SIZE_MM = 9;
const QR_X_MM = (LABEL_W_MM - QR_SIZE_MM) / 2;
const QR_Y_MM = MARGIN_MM;
const LINE1_Y_MM = QR_Y_MM + QR_SIZE_MM + 3.0;
const LINE2_Y_MM = LINE1_Y_MM + 3.2;

export function certificatePublicUrl(cert) {
  if (!cert?.certificate_number || !cert?.public_token) return null;
  return `${PUBLIC_BASE}/c/${cert.certificate_number}-${cert.public_token}`;
}

// "M" (no "H") a propósito: el QR lleva una URL, y con corrección alta los
// módulos quedan demasiado chicos para 9 mm.
async function qrDataUrl(payload) {
  return QRCode.toDataURL(String(payload), { errorCorrectionLevel: 'M', scale: 8, margin: 1 });
}

function drawLabel(doc, cert, dataUrl) {
  doc.addImage(dataUrl, 'PNG', QR_X_MM, QR_Y_MM, QR_SIZE_MM, QR_SIZE_MM);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(`Cert. ${cert.certificate_number}`, LABEL_W_MM / 2, LINE1_Y_MM, { align: 'center' });
  doc.text('Calins', LABEL_W_MM / 2, LINE2_Y_MM, { align: 'center' });
}

async function buildPdf(cert, preview) {
  const url = certificatePublicUrl(cert);
  if (!url) throw new Error('El certificado no tiene link público.');
  const dataUrl = preview || (await qrDataUrl(url));
  const doc = new jsPDF({ unit: 'mm', format: [LABEL_W_MM, LABEL_H_MM] });
  drawLabel(doc, cert, dataUrl);
  return doc;
}

export default function CertLabelModal({ open, onClose, certificate, instrument }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const url = useMemo(() => certificatePublicUrl(certificate), [certificate]);

  useEffect(() => {
    if (!open || !url) return;
    setPreview(null);
    setErr(null);
    qrDataUrl(url).then(setPreview).catch((e) => {
      console.error('[CertLabel] QR', e);
      setErr('No se pudo generar el QR.');
    });
  }, [open, url]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !certificate) return null;

  const handlePrint = async () => {
    try {
      setBusy(true);
      const doc = await buildPdf(certificate, preview);
      const blobUrl = URL.createObjectURL(doc.output('blob'));
      const win = window.open('');
      if (!win) {
        setErr('El navegador bloqueó la ventana de impresión. Permití las ventanas emergentes.');
        URL.revokeObjectURL(blobUrl);
        return;
      }
      win.document.write(`<!doctype html><html><head><title>Etiqueta ${certificate.certificate_number}</title></head>
<body style="margin:0"><iframe src="${blobUrl}" style="border:0;width:100%;height:100vh"
 onload="this.contentWindow.focus();this.contentWindow.print();"></iframe></body></html>`);
      win.document.close();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      console.error('[CertLabel] print', e);
      setErr(e?.message || 'No se pudo preparar la impresión.');
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setBusy(true);
      const doc = await buildPdf(certificate, preview);
      doc.save(`Etiqueta_Cert_${certificate.certificate_number}.pdf`);
    } catch (e) {
      console.error('[CertLabel] pdf', e);
      setErr(e?.message || 'No se pudo generar el PDF.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12161a] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Etiqueta del certificado</h3>
          <button onClick={onClose} className="px-2 py-1 text-sm rounded text-slate-300 hover:bg-white/10">Cerrar</button>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Vista previa a escala de la etiqueta real (20 × 18 mm) */}
          <div className="col-span-12 sm:col-span-5">
            <div className="rounded-lg border border-white/10 bg-white p-2 flex flex-col items-center justify-center">
              {preview ? (
                <>
                  <img src={preview} alt="QR" className="w-24 h-24 object-contain" />
                  <div className="text-[11px] font-bold text-black leading-tight mt-1">Cert. {certificate.certificate_number}</div>
                  <div className="text-[11px] font-bold text-black leading-tight">Calins</div>
                </>
              ) : (
                <div className="text-xs text-slate-500 py-10">Generando QR…</div>
              )}
            </div>
            <div className="text-[10px] text-slate-500 text-center mt-1">Cinta 18 mm · etiqueta 20 mm</div>
          </div>

          <div className="col-span-12 sm:col-span-7 space-y-2 text-sm">
            <div><span className="text-slate-500">Certificado: </span><span className="text-white font-semibold">{certificate.certificate_number}</span></div>
            {instrument && (
              <div><span className="text-slate-500">Instrumento: </span><span className="text-slate-200">#{instrument.lab_id} · {[instrument.brand, instrument.model].filter(Boolean).join(' ')}</span></div>
            )}
            <div className="text-xs">
              <span className="text-slate-500">El QR abre: </span>
              <span className="text-sky-300 break-all">{url}</span>
            </div>

            {err && <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-2 py-1.5">{err}</div>}

            <div className="pt-1 flex flex-wrap gap-2">
              <button onClick={handlePrint} disabled={busy || !preview}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-60">
                {busy ? 'Preparando…' : '🖨 Imprimir etiqueta'}
              </button>
              <button onClick={handleDownloadPdf} disabled={busy || !preview}
                className="px-3 py-2 rounded-lg border border-white/15 text-sm text-slate-200 hover:bg-white/5 disabled:opacity-60">
                Descargar PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
